'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  Suspense,
  lazy,
  useRef,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import useSWR from 'swr';
import customFetch from '@/lib/utils/fetch';
import PaymentSuccessModal from '@/components/Upgrade/PaymentSuccessModal';
import PaymentFailedModal from '@/components/Upgrade/PaymentFailedModal';
import CancelSubscriptionModal from '@/components/Upgrade/CancelSubscriptionModal';
import UnsubscribedSuccessModal from '@/components/Upgrade/UnsubscribedSuccessModal';
import useSWRMutation from 'swr/mutation';
import { getPlanInfo, getPlanType, PlanInfo } from '@/lib/stripe';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useTranslation } from '@/lib/i18n';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useStripe, type StripePrice } from '@/hooks/useStripe';

const SubscriptionModal = lazy(() => import('@/components/Upgrade/SubscriptionModal'));
const PackageModal = lazy(() => import('@/components/Upgrade/PackageModal'));

export interface PriceInfo {
  id: string;
  product: string;
  unit_amount: number;
  currency: string;
  recurring?: {
    interval: string;
    interval_count: number;
  };
}

export interface AllPriceInfo {
  plus_monthly?: PriceInfo;
  plus_yearly?: PriceInfo;
  pro_monthly?: PriceInfo;
  max_monthly?: PriceInfo;
  pro_yearly?: PriceInfo;
  max_yearly?: PriceInfo;
}

interface SubscriptionContextType {
  allPriceInfo: AllPriceInfo;
  priceInfoLoading: boolean;
  currentPlan: string;
  currentPlanInfo: PlanInfo;
  currentPlanName: string;
  currentPlanExp: string | null;
  currentSubscriptionId: string | null;
  currentPlanPriceInfo: PriceInfo | undefined;
  isSubscriped: boolean;
  isDowngradeScheduled: boolean;
  pendingPlanName: string;
  pendingBillingCycle: 'monthly' | 'yearly' | null;

  // usage
  usageLoading: boolean;
  searchUsed: number;
  researchUsed: number;
  searchTotalQuota: number | typeof Infinity;
  researchTotalQuota: number | typeof Infinity;
  searchRemainingCount: number | typeof Infinity;
  researchTotalRemainingCount: number | typeof Infinity;
  searchAllowed?: boolean;
  researchAllowed?: boolean;
  searchUsageCheckLoading: boolean;
  researchUsageCheckLoading: boolean;

  // Modal states
  isModalOpen: boolean;
  showSuccessModal: boolean;
  upgradedPlan: string;
  packageModalOpen: boolean;
  showCancelModal: boolean;
  showUnsubscribedSuccessModal: boolean;

  // Loading states
  isCanceling: boolean;
  isResubscribing: boolean;
  isDowngrading: boolean;
  isCancelingScheduledDowngrade: boolean;

  // Actions
  openModal: () => void;
  closeModal: () => void;
  handleSelectPlan: (planId: string, billingCycle: 'monthly' | 'yearly') => Promise<void>;
  verifyPaymentSuccess: (sessionId: string) => Promise<void>;
  handleSelectPackage: (packageId: string) => Promise<void>;
  handleUpgradePlan: (targetPriceKey: keyof AllPriceInfo) => Promise<void>;
  handleDowngradePlan: (targetPriceKey: keyof AllPriceInfo) => Promise<void>;
  handleConfirmCancel: () => Promise<void>;
  handleResubscribe: () => Promise<void>;
  handleCancelScheduledDowngrade: () => Promise<void>;
  setShowCancelModal: (show: boolean) => void;
  setShowUnsubscribedSuccessModal: (show: boolean) => void;
  mutateSubscriptionInfo: () => Promise<void>;
  mutateUsageInfo: () => Promise<void>;
  setPackageModalOpen: (value: boolean) => void;

  // buy loading
  isProcessing: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

// 从 useStripe({ prices: { key: 'forge' } }) 批量返回的价格里，按套餐等级 + 计费周期挑出对应一条
function pickForgePrice(
  prices: StripePrice[] | undefined,
  membershipLevel: 'plus' | 'max',
  interval: 'month' | 'year',
): PriceInfo | undefined {
  const price = prices?.find(
    (p) => p.metadata?.membership_level === membershipLevel && p.recurring?.interval === interval,
  );
  if (!price) return undefined;
  return {
    id: price.id,
    product: typeof price.product === 'string' ? price.product : price.product.id,
    unit_amount: price.unit_amount,
    currency: price.currency,
    recurring: price.recurring ?? undefined,
  };
}

/** 从 planType（如 monthly_plus / yearly_max）解析出套餐展示名，如 "Plus" / "Max" */
function planNameFromPlanType(planType?: string | null): string {
  if (!planType) return '';
  const parts = planType.split('_');
  const name = parts[parts.length - 1] ?? '';
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

/** 从 planType（如 monthly_plus / yearly_max）解析出计费周期 */
function billingCycleFromPlanType(planType?: string | null): 'monthly' | 'yearly' | null {
  if (!planType) return null;
  if (planType.startsWith('monthly_')) return 'monthly';
  if (planType.startsWith('yearly_')) return 'yearly';
  return null;
}

const SubscriptionProviderInner: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [upgradedPlan, setUpgradedPlan] = useState('');
  const [isPackagePurchase, setIsPackagePurchase] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUnsubscribedSuccessModal, setShowUnsubscribedSuccessModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isResubscribing, setIsResubscribing] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [isCancelingScheduledDowngrade, setIsCancelingScheduledDowngrade] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const { showError, showSuccess } = useSnackbar();
  const { t } = useTranslation();
  const sendLog = useAnalytics();

  // Guard to prevent double execution in StrictMode or re-renders
  const processedSessionIdRef = useRef<string | null>(null);
  const isVerifyingRef = useRef(false);

  const { trigger: setSearchLimitShow } = useSWRMutation('searchLimitShow');
  const { trigger: setReSearchLimitShow } = useSWRMutation('reSearchLimitShow');

  // Get user info
  const { data: userInfo } = useSWR('currentUserInfo');

  const { data: searchUsageCheckResult, isLoading: searchUsageCheckLoading } = useSWR(
    userInfo ? 'api:/user/usage/check?search_type=webSearch' : null,
  );
  const { data: researchUsageCheckResult, isLoading: researchUsageCheckLoading } = useSWR(
    userInfo ? 'api:/user/usage/check?search_type=deep_research' : null,
  );
  const {
    data: usageResult,
    mutate: mutateUsageInfo,
    isLoading: usageLoading,
  } = useSWR(userInfo ? 'api:/user/usage' : null);

  const { data: subscriptionResult, mutate: mutateSubscriptionInfo } = useSWR(
    userInfo?.membership?.currentSubscriptionId
      ? `api:/stripe/subscriptions/${userInfo?.membership?.currentSubscriptionId}`
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    },
  );

  // stripe forge product prices: key=forge 一次请求批量拉回 plus/max 月付+年付四个价格详情
  const { prices: forgePrices, pricesLoading: priceInfoLoading } = useStripe({
    prices: isModalOpen && userInfo ? { key: 'forge' } : false,
  });

  const allPriceInfo: AllPriceInfo = {
    plus_monthly: pickForgePrice(forgePrices, 'plus', 'month'),
    plus_yearly: pickForgePrice(forgePrices, 'plus', 'year'),
    max_monthly: pickForgePrice(forgePrices, 'max', 'month'),
    max_yearly: pickForgePrice(forgePrices, 'max', 'year'),
  };

  // currentPlan Get membership status from user info
  const currentPlan = userInfo?.membership?.status || 'free';

  // Get subscription expiration date from userInfo
  const currentPlanExp = userInfo?.membership?.expiredAt || null;

  // Get current subscription ID from userInfo.membership.currentSubscriptionId
  const currentSubscriptionId = userInfo?.membership?.currentSubscriptionId || null;

  // 预约中的降级 / 同档换周期：只有 pendingPlanType 会被写入，
  // 所以不能靠 isSubscriped 判断是否有预约中的变更。
  const pendingPlanType = subscriptionResult?.subscription?.pendingPlanType || null;
  const isDowngradeScheduled = !!pendingPlanType;

  // isSubscriped 的语义是「到期后仍然是付费订阅」。
  // 预约降级同样会把 cancelAtPeriodEnd 置为 true——那是它的实现手段（靠 Stripe 的到期取消
  // 结束当前套餐，再开新套餐），并不是用户要退订：到期后会切换到 pendingPlanType，
  // 用户依然在订阅中。只有 cancelAtPeriodEnd=true 且没有 pendingPlanType 才是真正的到期退订。
  // 漏掉这个判断的话，降级预约期间会错误地显示「到期日 / 重新订阅」，
  // 点击当前套餐还会误触发 handleResubscribe，把降级预约冲掉。
  const isSubscriped = !subscriptionResult?.subscription?.cancelAtPeriodEnd || isDowngradeScheduled;
  const pendingPlanName = planNameFromPlanType(pendingPlanType);
  const pendingBillingCycle = billingCycleFromPlanType(pendingPlanType);

  // Map plan to quota key
  const currentPlanInfo = getPlanInfo(currentPlan);
  const currentPlanPriceInfo =
    allPriceInfo?.[
      `${currentPlanInfo?.planType}_${currentPlanInfo?.billingCycle}` as keyof AllPriceInfo
    ];

  // Get total quotas based on plan
  const searchTotalQuota = usageResult?.data?.quotas?.search?.total ?? 0;
  const researchTotalQuota = usageResult?.data?.quotas?.research?.total ?? 0;
  const searchUsed = Number(usageResult?.data?.searchUsed ?? 0);
  const researchUsed = Number(usageResult?.data?.researchUsed ?? 0);

  const searchRemainingCount =
    searchTotalQuota - searchUsed >= 0 ? searchTotalQuota - searchUsed : 0;
  const researchTotalRemainingCount =
    researchTotalQuota - researchUsed >= 0 ? researchTotalQuota - researchUsed : 0;

  const searchAllowed = searchUsageCheckResult?.check_result?.allowed;
  const researchAllowed = researchUsageCheckResult?.check_result?.allowed;

  const verifyPaymentSuccess = useCallback(async (sessionId: string, from?: string) => {
    try {
      const response = await customFetch(`/api/stripe/checkout?session_id=${sessionId}`);
      if (response.ok) {
        const data = await response.json();

        // Check if this is a package purchase
        if (from === 'package') {
          setIsPackagePurchase(true);
          setShowSuccessModal(true);
        } else {
          // Regular subscription purchase
          const planType = data.session?.metadata?.plan_type || 'pro';
          let displayPlan = 'PRO';
          if (planType.includes('max')) {
            displayPlan = 'MAX';
          } else if (planType.includes('plus')) {
            displayPlan = 'PLUS';
          }
          setUpgradedPlan(displayPlan);
          setIsPackagePurchase(false);
          setShowSuccessModal(true);
        }

        // Clean up URL params
        const url = new URL(window.location.href);
        url.searchParams.delete('session_id');
        url.searchParams.delete('payment_success');
        url.searchParams.delete('from');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    }
  }, []);

  // Check for payment success or failure in URL params
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentSuccess = searchParams.get('payment_success');
    const paymentFailed = searchParams.get('payment_failed');
    const paymentFrom = searchParams.get('from');

    if (sessionId && paymentSuccess === 'true') {
      if (processedSessionIdRef.current === sessionId || isVerifyingRef.current) {
        return;
      }
      processedSessionIdRef.current = sessionId;
      isVerifyingRef.current = true;
      verifyPaymentSuccess(sessionId, paymentFrom || undefined).finally(() => {
        isVerifyingRef.current = false;
      });
    } else if (paymentFailed === 'true') {
      // Show payment failed modal
      setShowFailedModal(true);

      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('payment_failed');
      window.history.replaceState({}, '', url.toString());
      if (paymentFrom === 'package') {
        router.push('/usage');
      }
    }
  }, [searchParams, router, verifyPaymentSuccess]);

  useEffect(() => {
    if (searchAllowed) {
      setSearchLimitShow(false);
    } else {
      setSearchLimitShow(true);
    }
  }, [searchAllowed, setSearchLimitShow]);

  useEffect(() => {
    if (researchAllowed) {
      setReSearchLimitShow(false);
    } else {
      setReSearchLimitShow(true);
    }
  }, [researchAllowed, setReSearchLimitShow]);

  // 订阅会员
  const handleSelectPlan = async (planId: string, billingCycle: 'monthly' | 'yearly') => {
    if (!userInfo?.id) {
      console.error('User not logged in');
      return;
    }

    setIsProcessing(true);
    let response;

    try {
      // Determine the price ID based on plan and billing cycle
      const priceKey = `${planId}_${billingCycle}` as keyof AllPriceInfo;
      const priceId = allPriceInfo[priceKey]?.id;

      if (!priceId) {
        showError('Invalid plan selection');
        return;
      }
      const planType = getPlanType(planId, billingCycle);

      // Create checkout session
      response = await customFetch('/api/stripe/checkout', {
        method: 'POST',
        body: JSON.stringify({
          line_items: [
            {
              price: priceId,
              quantity: 1,
            },
          ],
          mode: 'subscription',
          success_url: `${window.location.origin}?payment_success=true&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}?payment_failed=true`,
          metadata: {
            user_id: userInfo.id,
            email: userInfo.email,
            plan_type: planType,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showError(errorData.error || 'Failed to create checkout session');
        return;
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
        sendLog('payment_click', {
          package: billingCycle,
        });
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 购买流量包
  const handleSelectPackage = async (packageId: string) => {
    if (!userInfo?.id) {
      console.error('User not logged in');
      return;
    }

    setIsProcessing(true);
    let response;

    try {
      response = await customFetch('/api/stripe/checkout', {
        method: 'POST',
        body: JSON.stringify({
          line_items: [
            {
              price: packageId,
              quantity: 1,
            },
          ],
          mode: 'payment',
          success_url: `${window.location.origin}?payment_success=true&session_id={CHECKOUT_SESSION_ID}&from=package`,
          cancel_url: `${window.location.origin}?payment_failed=true&from=package`,
          metadata: {
            user_id: userInfo.id,
            email: userInfo.email,
          },
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        showError(errorData.error || 'Failed to create checkout session');
        return;
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 升级会员
  const handleUpgradePlan = async (targetPriceKey: keyof AllPriceInfo) => {
    if (!userInfo?.id) {
      console.error('User not logged in');
      return;
    }

    setIsProcessing(true);
    let response;

    try {
      const currentPriceKey =
        `${currentPlanInfo?.planType}_${currentPlanInfo?.billingCycle}` as keyof AllPriceInfo;
      const currentPriceId = allPriceInfo[currentPriceKey]?.id;
      const targetPriceId = allPriceInfo[targetPriceKey]?.id;
      if (!currentPriceId || !targetPriceId) {
        showError('Invalid plan selection');
        return;
      }
      response = await customFetch('/api/stripe/subscriptions/upgrade-payment', {
        method: 'POST',
        body: JSON.stringify({
          current_price_id: currentPriceId,
          target_price_id: targetPriceId,
          success_url: `${window.location.origin}?payment_success=true&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}?payment_failed=true`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showError(errorData.error || 'Failed to create checkout session');
        return;
      }

      const data = await response.json();

      // 当前订阅的剩余价值已经覆盖新套餐价格，后端直接切好了套餐、没有产生 Checkout。
      // 这里不跳转，刷新订阅信息让界面切到新套餐即可。
      if (data.no_payment_required) {
        await mutateSubscriptionInfo();
        await mutateUsageInfo();
        showSuccess(t('Upgrade successful!'));
        return;
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 降级 / 同档换周期：预约到期生效，不立即扣款，所以不像升级那样跳转 Stripe Checkout
  const handleDowngradePlan = async (targetPriceKey: keyof AllPriceInfo) => {
    if (!userInfo?.id) {
      console.error('User not logged in');
      return;
    }

    const targetPriceId = allPriceInfo[targetPriceKey]?.id;
    if (!targetPriceId) {
      showError('Invalid plan selection');
      return;
    }

    try {
      setIsDowngrading(true);
      const response = await customFetch('/api/stripe/subscriptions/downgrade', {
        method: 'POST',
        body: JSON.stringify({
          target_price_id: targetPriceId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showError(errorData.error || 'Failed to schedule downgrade');
        return;
      }

      await mutateSubscriptionInfo();
      showSuccess(t('Downgrade scheduled successfully!'));
    } catch (error) {
      console.error('Error scheduling downgrade:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError('Failed to schedule downgrade: ' + errorMessage);
    } finally {
      setIsDowngrading(false);
    }
  };

  // 取消订阅
  const handleConfirmCancel = async () => {
    // Check if currentSubscriptionId is valid
    if (!currentSubscriptionId) {
      console.error('No subscription ID found');
      showError('No subscription ID found. Please refresh and try again.');
      setShowCancelModal(false);
      return;
    }

    try {
      setIsCanceling(true);
      const response = await customFetch(`/api/stripe/subscriptions/cancel`, {
        method: 'POST',
        body: JSON.stringify({
          subscriptionId: currentSubscriptionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      const data = await response.json();
      console.log('Subscription cancelled successfully:', data);

      // Refresh user info to update subscription status
      mutateSubscriptionInfo();

      // Close cancel modal and show success modal
      setShowCancelModal(false);
      setShowUnsubscribedSuccessModal(true);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError('Failed to cancel subscription: ' + errorMessage);
    } finally {
      setIsCanceling(false);
    }
  };

  // 重新订阅
  const handleResubscribe = async () => {
    // 检查是否有有效的订阅ID
    if (!currentSubscriptionId) {
      console.error('No subscription ID found');
      showError('No subscription ID found. Please refresh and try again.');
      return;
    }

    try {
      setIsResubscribing(true);

      const response = await customFetch(`/api/stripe/subscriptions/resume`, {
        method: 'POST',
        body: JSON.stringify({
          subscriptionId: currentSubscriptionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resume subscription');
      }

      const data = await response.json();
      console.log('Subscription resumed successfully:', data);

      // 刷新订阅信息
      mutateSubscriptionInfo();

      showSuccess(t('Subscription resumed successfully!'));
    } catch (error) {
      console.error('Error resuming subscription:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError('Failed to resume subscription: ' + errorMessage);
    } finally {
      setIsResubscribing(false);
    }
  };

  // 取消已预约的降级 / 同档换周期，恢复到期自动续费原套餐
  const handleCancelScheduledDowngrade = async () => {
    try {
      setIsCancelingScheduledDowngrade(true);

      const response = await customFetch('/api/stripe/subscriptions/downgrade', {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel scheduled downgrade');
      }

      await mutateSubscriptionInfo();
      showSuccess(t('Scheduled downgrade canceled'));
    } catch (error) {
      console.error('Error canceling scheduled downgrade:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError('Failed to cancel scheduled downgrade: ' + errorMessage);
    } finally {
      setIsCancelingScheduledDowngrade(false);
    }
  };

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const value: SubscriptionContextType = {
    allPriceInfo,
    priceInfoLoading,
    currentPlan,
    currentPlanInfo,
    currentPlanName: currentPlanInfo.displayName,
    currentPlanExp,
    currentSubscriptionId,
    currentPlanPriceInfo,
    isSubscriped,
    isDowngradeScheduled,
    pendingPlanName,
    pendingBillingCycle,

    usageLoading,
    searchTotalQuota,
    researchTotalQuota,
    searchUsed,
    researchUsed,
    searchRemainingCount,
    researchTotalRemainingCount,
    searchAllowed,
    researchAllowed,
    searchUsageCheckLoading,
    researchUsageCheckLoading,

    isModalOpen,
    showSuccessModal,
    upgradedPlan,
    packageModalOpen,
    showCancelModal,
    showUnsubscribedSuccessModal,
    isCanceling,
    isResubscribing,
    isDowngrading,
    isCancelingScheduledDowngrade,
    openModal,
    closeModal,
    handleSelectPlan,
    verifyPaymentSuccess,
    isProcessing,
    handleSelectPackage,
    handleUpgradePlan,
    handleDowngradePlan,
    handleConfirmCancel,
    handleResubscribe,
    handleCancelScheduledDowngrade,
    setShowCancelModal,
    setShowUnsubscribedSuccessModal,
    mutateSubscriptionInfo,
    mutateUsageInfo,

    setPackageModalOpen,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}

      {/* Global Subscription Modals */}
      <SubscriptionModal
        currentPlanInfo={currentPlanInfo}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSelectPlan={handleSelectPlan}
        currentPlan={currentPlan}
        isLoading={isProcessing}
      />

      <PaymentSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setIsPackagePurchase(false); // Reset the flag when closing
        }}
        planName={upgradedPlan}
        isPackage={isPackagePurchase}
      />

      <PaymentFailedModal
        isOpen={showFailedModal}
        onClose={() => {
          setShowFailedModal(false);
        }}
        onTryAgain={() => {
          setShowFailedModal(false);
          setIsModalOpen(true);
        }}
      />
      <PackageModal open={packageModalOpen} setOpen={setPackageModalOpen} />

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
        isCanceling={isCanceling}
      />

      <UnsubscribedSuccessModal
        isOpen={showUnsubscribedSuccessModal}
        onClose={() => setShowUnsubscribedSuccessModal(false)}
      />
    </SubscriptionContext.Provider>
  );
};

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  return (
    <Suspense fallback={null}>
      <SubscriptionProviderInner>{children}</SubscriptionProviderInner>
    </Suspense>
  );
};
